import {
    ClosePickerOptions,
    SearchPickerOption,
    SearchPickerSettings,
} from '@trustoo/components/searchPicker/SearchPickerContext'
import { useCallback, useEffect, useRef, useState } from 'react'

interface UsePickerSearchParams {
    initialQuery: string
    initialOptions: SearchPickerOption[]
    fetchFunction?: (query: string) => Promise<SearchPickerOption[]>
    selectedOption?: SearchPickerOption
    onSelectOption?: (option: SearchPickerOption) => void
    settings: SearchPickerSettings
    setIsOpen: (isOpen: boolean) => void
}

const usePickerSearch = ({
    initialQuery,
    initialOptions,
    fetchFunction,
    selectedOption,
    onSelectOption,
    settings,
    setIsOpen,
}: UsePickerSearchParams) => {
    const [query, setQuery] = useState(initialQuery)
    const [options, setOptions] = useState<SearchPickerOption[]>(initialOptions)
    const [isLoading, setIsLoading] = useState(false)
    const [hasNoResults, setHasNoResults] = useState(false)
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const search = useCallback(
        (newQuery: string) => {
            setQuery(newQuery)

            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
            }

            if (!fetchFunction) {
                return
            }

            if (newQuery.length === 0) {
                setOptions(initialOptions)
                setHasNoResults(false)
                setIsLoading(false)
                return
            }

            if (selectedOption && newQuery === selectedOption.query) {
                setIsLoading(false)
                return
            }

            setIsLoading(true)
            setHasNoResults(false)

            debounceTimeoutRef.current = setTimeout(() => {
                fetchFunction(newQuery)
                    .then((fetchedOptions) => {
                        setOptions(fetchedOptions)
                        setHasNoResults(fetchedOptions.length === 0)
                    })
                    .catch((error) => {
                        console.error('Error fetching search options:', error)
                        setOptions([])
                        setHasNoResults(true)
                    })
                    .finally(() => {
                        setIsLoading(false)
                    })
            }, settings.debounceMs)
        },
        [fetchFunction, selectedOption, initialOptions, settings.debounceMs],
    )

    const closePicker = useCallback(
        (closeOptions?: ClosePickerOptions) => {
            if (closeOptions?.selectOption) {
                onSelectOption?.(closeOptions.selectOption)
                setQuery(closeOptions.selectOption.query)
            } else if (closeOptions?.autoSelect && options.length === 1) {
                onSelectOption?.(options[0])
                setQuery(options[0].query)
            } else if (selectedOption) {
                if (query !== selectedOption.query) {
                    setQuery(selectedOption.query)
                }
            } else if (settings.clearUnselectedQuery && query !== '') {
                search('')
            }
            setIsOpen(false)
        },
        [
            options,
            onSelectOption,
            selectedOption,
            query,
            settings.clearUnselectedQuery,
            search,
            setIsOpen,
        ],
    )

    useEffect(() => {
        setQuery(selectedOption?.query ?? '')
    }, [selectedOption])

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
            }
        }
    }, [])

    return {
        query,
        setQuery,
        search,
        options,
        setOptions,
        isLoading,
        hasNoResults,
        closePicker,
    }
}

export default usePickerSearch
